/// <reference types="@types/google.maps" />

import { Component, OnInit, OnDestroy, Injectable, ViewChild } from '@angular/core';
import { StartupService } from '../services/startup.service';
import { GpsLocation } from '../model/gpslocation';
import { List } from 'immutable';
import { Availability } from '../model/availability';
import { ActivityActorEvent } from '../model/activityActor-event';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { UserInfo } from '../model/userInfo';
import { UserMapping } from '../model/usermapping';
import { SettingsService } from '../services/settings.service';
import { SignalRService } from "../services/signalr.service";
import { UntilDestroy } from '@ngneat/until-destroy';
import { StateMachineEventEnum } from '../model/stateMachineEvent';
import { MapInfoWindow, MapMarker } from '@angular/google-maps';
import { BusMapConstants } from './busmap.constants';
import { WorkShiftService } from '../services/workShift.service';
import { UserMappingService } from '../services/usermapping.service';
import { ActivityActorService } from '../services/activityActor.service';

@Component({
  selector: 'app-busmap',
  templateUrl: './busmap.component.html',
  styleUrls: ['./busmap.component.css']
})

@UntilDestroy()
@Injectable()
export class BusmapComponent implements OnInit, OnDestroy {

  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow

  readonly MarkerIconAirsideActive = {
    url: BusMapConstants.MarkerAirsideImageName,
    labelOrigin: new google.maps.Point(10, 40)
  };
  readonly MarkerIconAirsideInactive = {
    url: BusMapConstants.MarkerAirsideInactiveImageName,
    labelOrigin: new google.maps.Point(10, 40)
  }
  readonly MarkerIconLandsideActive = {
    url: BusMapConstants.MarkerLandsideImageName,
    labelOrigin: new google.maps.Point(10, 40)
  };
  readonly MarkerIconLandsideInactive = {
    url: BusMapConstants.MarkerLandsideInactiveImageName,
    labelOrigin: new google.maps.Point(10, 40)
  }
  readonly MarkerIconUnknownActive = {
    url: BusMapConstants.MarkerUnknownActiveImageName,
    labelOrigin: new google.maps.Point(10, 30)
  }
  readonly MarkerIconUnknownInactive: google.maps.Icon = {
    url: BusMapConstants.MarkerUnknownInactiveImageName,
    labelOrigin: new google.maps.Point(10, 30)
  }

  center: google.maps.LatLngLiteral = { lat: BusMapConstants.DefaultLatitudeARN, lng: BusMapConstants.DefaultLongitudeARN };

  /**
   * Local variables
   */

  public mapOptions: google.maps.MapOptions;

  public infoContent: InfoContent;

  private mapClickListener: google.maps.MapsEventListener;

  private gpsLocations: GpsLocation[] = [];
  private availabilityResources: Availability[] = [];
  private userMappings: List<UserMapping> = List<UserMapping>();
  private activityActorEvents: ActivityActorEvent[];
  private locationMarkers: { id: string, marker: google.maps.Marker; }[] = [];
  private timerId = null;

  private resourcesSubscription: Subscription;
  private userMappingSubscription: Subscription;
  private activitySubscription: Subscription;
  private logOutSubscription: Subscription;
  private updateGpsSubscription: Subscription;

  private updateActivitySubscription: Subscription;
  private deleteActivitySubscription: Subscription;

  markers: google.maps.Marker[] = [];

  constructor(
    public startupService: StartupService,
    public settings: SettingsService,
    private readonly signalRservice: SignalRService,
    private readonly workShiftService: WorkShiftService,
    private readonly usermappingService: UserMappingService,
    private readonly activityActorService: ActivityActorService) {

  }

  private display: google.maps.LatLngLiteral;

  moveMap(event: google.maps.MapMouseEvent) {
    this.center = (event.latLng.toJSON());
  }

  ngOnInit() {
    this.mapOptions = {
      center: this.center,
      zoom: BusMapConstants.DefaultZoomLevel,
      streetViewControl: false,
      mapTypeControl: false,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] }
      ]
    };

    this.availabilityResources = this.workShiftService.availabilities;

  }

  ngOnDestroy() {

    if (this.mapClickListener) {
      this.mapClickListener.remove();
    }
    this.removeSubscriptions();

  }

  ngAfterViewInit() {

    this.setupSubscriptions();

    this.logOutSubscription = this.signalRservice.logout$.subscribe((userInfo: UserInfo) => {
      let index = this.gpsLocations.findIndex(x => x.gpsId === userInfo.gpsId);
      if (index >= 0) {
        this.gpsLocations.splice(index, 1);

        this.refreshLocations(this.gpsLocations);
      }

    });

    this.updateGpsSubscription = this.signalRservice.updateGpsLocation$.subscribe(location => {
      let index = this.gpsLocations ? this.gpsLocations.findIndex(x => x.gpsId === location.gpsId) : -1;
      console.info("GpsLocationUpdate Lat: " + location.latitude + " Long: " + location.longitude + " userid: " + location.userMappingId);

      if (index >= 0) {
        this.gpsLocations[index] = location;
      }
      else {
        this.gpsLocations.push(location);
      }

      this.refreshLocations(this.gpsLocations);

    });

  }

  private removeSubscriptions() {

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.updateActivitySubscription) {
      this.updateActivitySubscription.unsubscribe();
    }
    if (this.deleteActivitySubscription) {
      this.deleteActivitySubscription.unsubscribe();
    }

    if (this.logOutSubscription) {
      this.logOutSubscription.unsubscribe();
    }

    if (this.updateGpsSubscription) {
      this.updateGpsSubscription.unsubscribe();
    }

    if (this.resourcesSubscription) {
      this.resourcesSubscription.unsubscribe();
    }

    if (this.userMappingSubscription)
    {
      this.userMappingSubscription.unsubscribe();
    }

    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }

  }

  private setupSubscriptions() {
    // Get the list of users/availabilities
    if (!this.resourcesSubscription) {
      this.resourcesSubscription = this.workShiftService.workShiftGroupsUpdated$.subscribe(() => {
        this.availabilityResources = this.workShiftService.availabilities;
      });
    }

    if (!this.userMappingSubscription) {
      // Get the list of usermapping info
      this.userMappingSubscription = this.usermappingService.UserMappings.subscribe((result: List<UserMapping>) => {
        this.userMappings = result;
        console.log("Map: UserMappings subscription done");
      });
    }

    if (!this.activitySubscription) {
      // Get the list of assignments
      this.activityActorEvents = this.activityActorService.activityActors;
      this.activitySubscription = this.activityActorService.activityActorLoaded$.subscribe((result: ActivityActorEvent[]) => {
        this.activityActorEvents = result;
        console.log("Map: ActivityActorEvents subscription done");

      });
    }

    this.updateActivitySubscription = this.activityActorService.updateActivity$.subscribe((activity: ActivityActorEvent) => {
      let index = this.activityActorEvents.findIndex(x => x.activityActor.id == activity.activityActor.id);
      if (index >= 0) {
        this.activityActorEvents[index] = activity;
      }
      else {
        this.activityActorEvents.push(activity);
      }
    });

    this.deleteActivitySubscription = this.activityActorService.deleteActivity$.subscribe((activity: ActivityActorEvent) => {
      let index = this.activityActorEvents.findIndex(x => x.activityActor.id == activity.activityActor.id);
      if (index >= 0) {
        this.activityActorEvents.splice(index, 1);
      }
    });


    if (!this.timerId) {

      // set timeout so the display updates even if we dont get any new notification
      this.timerId = setInterval(() => {
        this.refreshLocations(this.gpsLocations);
      }, 5000);
    }
  }

  // Loop thru and only show the first location for each gpsid
  private refreshLocations(locations: GpsLocation[]) {
    if (locations) {
      locations.forEach(location => {
        this.displayLocation(location);
      });
    }
  }

  // Display the location on the map indicating position, time, and if the location is old
  private displayLocation(location: GpsLocation) {

    if (this.availabilityResources.length === 0) return;

    let markerPosition = new google.maps.LatLng(location.latitude, location.longitude);

    let marker: google.maps.Marker = this.locationMarkers[location.gpsId];
    //let marker: MapMarker = this.locationMarkers[location.gpsId];
    let displayText = 'Not scheduled';

    let resource = this.availabilityResources.find(a => a.userMappingID == location.userMappingId);
    let driverSkill = "";
    let driverName = "-----";

    // Get the driver name and skill
    if (resource) {
      driverSkill = resource.skillExpanded ? resource.skillExpanded[0] : "";
      if (resource.name != undefined) {
        driverName = resource.name;
      }
      let meta = resource.metaExpanded;
      if (meta) {
        displayText = meta.BusNumber;
      }

    } else {
      // Driver not found in resources that are working today? Look them up in usermappings
      let usermapping = this.userMappings.find(u => u.id == location.userMappingId);
      if (usermapping) {
        driverName = usermapping.name;
      }
    }

    if (marker) {
      marker.setPosition(markerPosition);
      marker['location'] = new GpsLocation(location);
      let label = marker.getLabel() as google.maps.MarkerLabel;
      label.color = "#7492B3";
      label.text = displayText;

      marker.setLabel(label);

    } else {
      marker = this.makeMapsMarker(location, driverName);
      this.locationMarkers[location.gpsId] = marker;
      this.markers.push(marker);
    }

    let event = this.getCurrentEvent(location.userMappingId);
    let isInactive: boolean = true;
    if (event) {
      isInactive = this.isStatusInActive(event.activityActor.state);
    }

    let image;
    if (driverSkill == BusMapConstants.AvailabilitySkillAirside) {
      image = isInactive ? this.MarkerIconAirsideInactive : this.MarkerIconAirsideActive;

    } else if (driverSkill == BusMapConstants.AvailabilitySkillLandside) {
      image = this.MarkerIconLandsideActive;

    } else {
      image = isInactive ? this.MarkerIconUnknownInactive : this.MarkerIconUnknownActive;
    }

    marker.setIcon(image);
    marker['location'] = new GpsLocation(location);
  }

  private isStatusInActive(type: string) {
    switch (type) {

      case StateMachineEventEnum.Start:
      case StateMachineEventEnum.ArriveEmpty:
      case StateMachineEventEnum.Loading:
      case StateMachineEventEnum.Driving:
      case StateMachineEventEnum.ArriveFull:
      case StateMachineEventEnum.Unloading: {
        return false;
      }
      case StateMachineEventEnum.Assign:
      default:
        return true;
    }
  }

  private makeMapsMarker(location: GpsLocation, label: string) {

    let marker = new google.maps.Marker({
      position: {
        lat: location.latitude,
        lng: location.longitude
      },
      label: {
        text: label,
        fontWeight: "bold",
        fontSize: "22px"
      },
      animation: google.maps.Animation.BOUNCE,
      clickable: true
    });

    return marker;
  }

  private getCurrentEvent(userMappingId: string): ActivityActorEvent {
    let events = this.activityActorEvents.filter(a => a.activityActor.userMappingID == userMappingId && a.activityActor.status == BusMapConstants.StateMachineActiveStatus);
    if (events.length > 0) {
      let event = events.find(e => !this.isStatusInActive(e.activityActor.state));
      return event;
    }
    return undefined;
  }

  public displayLocationPopup(marker: MapMarker, content) {
    let location = content['location']
    marker['popupVisible'] = true;
    let pipe = new DatePipe(this.settings.getCurrentLocale());

    let infoContent = new InfoContent();
    infoContent.lastUpdated = pipe.transform(location.time, "mediumTime");
    infoContent.assignment = "Inget aktivt uppdrag";

    let event = this.getCurrentEvent(location.userMappingId);


    if (event) {
      infoContent.assignment = event ? event.activityActor.actor.name : "--";
      infoContent.start = event ? pipe.transform(event.start.toDate(), "shortTime") : "--";
      infoContent.end = event ? pipe.transform(event.end.toDate(), "shortTime") : "--";
      infoContent.inactive = this.isStatusInActive(event.activityActor.state)
    }

    let resource = this.availabilityResources.find(a => a.userMappingID == location.userMappingId);
    if (resource) {
      infoContent.driver = resource.name;
    }

    this.infoContent = infoContent;
    this.infoWindow.open(marker);
  }

}


class InfoContent {
  driver: string;
  start: string;
  end: string;
  lastUpdated: string;
  inactive: boolean;
  assignment: string;
}
